import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/base/AnimatedSection';
import AnimatedImage from '../../components/base/AnimatedImage';
import HeroSlider from '../../components/feature/HeroSlider';
import Navigation from '../../components/feature/Navigation';

export default function HomePage() {
  return (
    <div className="min-h-screen">
      {/* Navigation Header */}
      <Navigation />

      {/* Hero Slider */}
      <HeroSlider />

      {/* Product Categories */}
      <AnimatedSection className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 stagger-children">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Turf Solutions for Every Need</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">From residential lawns to professional athletic fields, we provide premium synthetic turf for all applications in Tulsa and beyond</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Landscape Turf',
                description: 'Beautiful, maintenance-free lawns for homes and businesses',
                image: 'https://public.readdy.ai/ai/img_res/89776a5965b5d8b73aaa92cab759af33.jpg',
                link: '/landscape-turf'
              },
              {
                title: 'Pet Turf',
                description: 'Durable, pet-friendly turf with superior drainage',
                image: 'https://readdy.ai/api/search-image?query=happy%20golden%20retriever%20dog%20playing%20on%20clean%20green%20synthetic%20pet%20turf%20artificial%20grass%20in%20backyard%20simple%20neutral%20background%20professional%20pet%20photography%20bright%20natural%20lighting%20showing%20safe%20pet%20friendly%20surface&width=600&height=400&seq=petturf918&orientation=landscape',
                link: '/pet-turf'
              },
              {
                title: 'Athletic Turf',
                description: 'Professional-grade surfaces for sports facilities',
                image: 'https://readdy.ai/api/search-image?query=professional%20sports%20field%20with%20green%20synthetic%20athletic%20turf%20artificial%20grass%20football%20soccer%20field%20markings%20clean%20simple%20background%20stadium%20photography%20bright%20daylight%20showing%20durable%20performance%20surface&width=600&height=400&seq=athletic918&orientation=landscape',
                link: '/athletic-turf'
              },
              {
                title: 'Putting Greens',
                description: 'Custom backyard golf greens with true roll',
                image: 'https://readdy.ai/api/search-image?query=backyard%20putting%20green%20with%20synthetic%20golf%20turf%20artificial%20grass%20golf%20ball%20and%20flag%20clean%20simple%20background%20professional%20golf%20photography%20bright%20natural%20light%20showing%20smooth%20putting%20surface&width=600&height=400&seq=putting918&orientation=landscape',
                link: '/putting-green-turf'
              }
            ].map((category, index) => (
              <Link 
                key={category.title} 
                to={category.link} 
                className="group cursor-pointer"
                style={{ 
                  opacity: 0,
                  animation: 'fadeIn 400ms ease-out forwards',
                  animationDelay: `${index * 100}ms`
                }}
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 h-full" style={{ transform: 'translateY(0)' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div className="h-64 w-full overflow-hidden">
                    <img 
                      src={category.image} 
                      alt={category.title} 
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{category.title}</h3>
                    <p className="text-gray-600 mb-4">{category.description}</p>
                    <span className="text-green-700 font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all duration-200">
                      Learn More <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Wholesale Section */}
      <AnimatedSection className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="stagger-children">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Wholesale Pricing for Contractors & Landscapers</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">918Turf is Tulsa's trusted wholesale distributor of premium synthetic turf. We supply contractors, landscapers, builders, and facility managers throughout Oklahoma with top-quality artificial grass at competitive wholesale prices.</p>
              <ul className="space-y-4 mb-8">
                {[
                  'Volume discounts available',
                  'Fast delivery or pickup in Tulsa',
                  'Expert installation guidance',
                  'Made in USA products',
                  '10-year warranty on all turf'
                ].map((item, index) => (
                  <li 
                    key={item} 
                    className="flex items-start gap-3"
                    style={{ 
                      opacity: 0,
                      animation: 'fadeIn 400ms ease-out forwards',
                      animationDelay: `${index * 80}ms`
                    }}
                  >
                    <i className="ri-checkbox-circle-fill text-green-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <a href="tel:918-806-8873" className="bg-green-700 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-green-800 hover:shadow-xl transition-all duration-200 whitespace-nowrap cursor-pointer inline-block" style={{ transform: 'translateY(0)' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>Request Wholesale Quote</a>
            </div>
            <AnimatedImage 
              src="https://readdy.ai/api/search-image?query=professional%20landscaper%20contractor%20installing%20rolling%20out%20green%20synthetic%20turf%20artificial%20grass%20in%20backyard%20construction%20work%20scene%20clean%20simple%20background%20professional%20photography%20bright%20natural%20daylight&width=800&height=600&seq=wholesale918&orientation=landscape" 
              alt="Wholesale Turf Distribution Tulsa" 
              className="rounded-2xl shadow-2xl h-96 w-full"
            />
          </div>
        </div>
      </AnimatedSection>

      {/* Why Choose Us */}
      <AnimatedSection className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 stagger-children">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Choose 918Turf?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Your local Tulsa turf experts committed to quality, service, and value</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-flag-fill',
                title: 'Made in USA',
                description: 'All our synthetic turf is proudly manufactured in the United States with premium materials and rigorous quality control'
              },
              {
                icon: 'ri-map-pin-fill',
                title: 'Local Tulsa Service',
                description: 'Based in Tulsa, we understand Oklahoma weather and provide fast local delivery throughout the metro area'
              },
              {
                icon: 'ri-shield-check-fill',
                title: '10-Year Warranty',
                description: 'Every product comes with a comprehensive 10-year warranty covering manufacturing defects and UV stability'
              },
              {
                icon: 'ri-price-tag-3-fill',
                title: 'Wholesale Pricing',
                description: 'Direct-from-distributor pricing means you save money without sacrificing quality or service'
              },
              {
                icon: 'ri-tools-fill',
                title: 'Installation Support',
                description: 'Expert guidance and all the supplies you need for successful turf installation projects'
              },
              {
                icon: 'ri-customer-service-2-fill',
                title: 'Expert Consultation',
                description: 'Our knowledgeable team helps you select the perfect turf for your specific application and budget'
              }
            ].map((feature, index) => (
              <div 
                key={feature.title} 
                className="text-center p-8 rounded-2xl hover:bg-gray-50 transition-all duration-300"
                style={{ 
                  opacity: 0,
                  animation: 'fadeIn 400ms ease-out forwards',
                  animationDelay: `${index * 100}ms`,
                  transform: 'translateY(0)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6 transition-colors duration-200 hover:bg-green-200">
                  <i className={`${feature.icon} text-3xl text-green-700`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Service Area */}
      <AnimatedSection className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 stagger-children">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Serving Tulsa & Surrounding Areas</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Fast delivery and pickup available throughout the Tulsa metro area and beyond</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="h-96 w-full rounded-2xl overflow-hidden shadow-2xl">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d207429.0876255449!2d-96.14070784999999!3d36.1539816!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87b6923f0b2d7c5b%3A0x7c9b0b3d0b0b0b0b!2sTulsa%2C%20OK!5e0!3m2!1sen!2sus!4v1234567890" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy"
                className="transition-opacity duration-300"
              ></iframe>
            </div>
            <div className="stagger-children">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">We Proudly Serve:</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  'Tulsa',
                  'Broken Arrow',
                  'Bixby',
                  'Owasso',
                  'Jenks',
                  'Sand Springs',
                  'Sapulpa',
                  'Glenpool',
                  'Claremore',
                  'Coweta',
                  'Catoosa',
                  'Collinsville'
                ].map((city, index) => (
                  <div 
                    key={city} 
                    className="flex items-center gap-2"
                    style={{ 
                      opacity: 0,
                      animation: 'fadeIn 400ms ease-out forwards',
                      animationDelay: `${index * 60}ms`
                    }}
                  >
                    <i className="ri-map-pin-line text-green-600"></i>
                    <span className="text-gray-700">{city}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-600 mt-8 leading-relaxed">Delivery available to other Oklahoma cities. Contact us for shipping options and rates.</p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection className="py-24 bg-green-700">
        <div className="max-w-4xl mx-auto px-6 text-center stagger-children">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Transform Your Space?</h2>
          <p className="text-xl text-white/95 mb-10 leading-relaxed">Contact 918Turf today for expert advice, product samples, and competitive wholesale pricing on premium synthetic turf</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:918-806-8873" className="bg-white text-green-700 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 hover:shadow-xl transition-all duration-200 whitespace-nowrap cursor-pointer inline-block" style={{ transform: 'translateY(0)' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>Call 918-806-8873</a>
            <a href="mailto:info@918turf.com" className="bg-green-800 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-green-900 hover:shadow-xl transition-all duration-200 whitespace-nowrap cursor-pointer inline-block" style={{ transform: 'translateY(0)' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>Email Us</a>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
}
